import { useEffect, useState } from 'react'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { actualizarMiPerfil } from '../../lib/portalClienteApi'
import { supabase } from '../../lib/supabase'
import TurnstileWidget from '../../components/TurnstileWidget'

/**
 * /cliente/perfil
 *
 * Refactor 2026-06-04: la pantalla usaba shell propia (navbar + hero claro)
 * que rompía la consistencia con el portal cliente dark. Migrada a
 * ClienteLayout con dark mode + CSS vars --cl-*.
 */
export default function ClientePerfil() {
  const { user, profile, refreshProfile } = useAuth()
  const [f, setF] = useState({ nombre: '', phone: '' })
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [err, setErr] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [showCambiarPwd, setShowCambiarPwd] = useState(false)

  useEffect(() => {
    if (profile) setF({ nombre: profile.nombre || '', phone: profile.phone || '' })
  }, [profile])

  async function guardar() {
    setSaving(true); setErr(null)
    try {
      await actualizarMiPerfil(f)
      if (refreshProfile) await refreshProfile()
      setSavedAt(new Date())
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  async function cambiarPassword() {
    if (!turnstileToken) {
      alert('Esperá un instante, todavía se está cargando la verificación anti-bot.')
      return
    }
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/set-password`,
      captchaToken: turnstileToken,
    })
    if (error) alert('Error: ' + error.message)
    else {
      alert('Te mandamos un email con el link para cambiar tu contraseña.')
      setShowCambiarPwd(false)
    }
  }

  return (
    <ClienteLayout title="Editar perfil">
      <div className="pf-wrap">
        <div className="pf-headline">
          <h1>Editar perfil</h1>
          <p>Actualizá tu nombre, teléfono y contraseña.</p>
        </div>

        {/* Card datos personales */}
        <div className="pf-card">
          <h3 className="pf-card-h">Datos personales</h3>

          <label className="pf-field">
            <span className="pf-field-l">Nombre completo</span>
            <input
              type="text"
              value={f.nombre}
              onChange={e => setF({ ...f, nombre: e.target.value })}
              placeholder="Juan Pérez"
            />
          </label>

          <label className="pf-field">
            <span className="pf-field-l">Teléfono</span>
            <input
              type="tel"
              value={f.phone}
              onChange={e => setF({ ...f, phone: e.target.value })}
              placeholder="098 123 456"
            />
          </label>

          <label className="pf-field">
            <span className="pf-field-l">Email</span>
            <input type="email" value={user?.email || ''} disabled />
            <span className="pf-hint">Para cambiar tu email contactanos por WhatsApp.</span>
          </label>

          {err && <div className="pf-err">{err}</div>}
          {savedAt && <div className="pf-ok">✓ Guardado</div>}

          <div className="pf-actions">
            <button className="pf-btn-primary" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>

        {/* Card seguridad */}
        <div className="pf-card">
          <h3 className="pf-card-h">🔐 Seguridad</h3>
          <p className="pf-card-sub">Cambiá tu contraseña con un email de reset.</p>
          {!showCambiarPwd ? (
            <button className="pf-btn-secondary" onClick={() => setShowCambiarPwd(true)}>
              Cambiar contraseña
            </button>
          ) : (
            <>
              <TurnstileWidget action="cambio-password" onToken={setTurnstileToken} />
              <div className="pf-actions" style={{ marginTop: 10 }}>
                <button
                  className="pf-btn-secondary"
                  onClick={() => { setShowCambiarPwd(false); setTurnstileToken(null) }}
                >
                  Cancelar
                </button>
                <button
                  className="pf-btn-primary"
                  onClick={cambiarPassword}
                  disabled={!turnstileToken}
                >
                  Mandar email de reset
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .pf-wrap { max-width: 720px; margin: 0 auto; padding: 16px 16px 32px }
        .pf-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .pf-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 6px; line-height: 1.55 }

        .pf-card {
          margin-top: 18px; padding: 22px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 16px;
          display: flex; flex-direction: column; gap: 14px;
        }
        .pf-card-h {
          font-size: 16px; font-weight: 900; color: var(--cl-text);
          margin: 0; letter-spacing: -.01em;
        }
        .pf-card-sub {
          font-size: 13px; color: var(--cl-muted);
          margin: 0; line-height: 1.55;
        }

        .pf-field { display: flex; flex-direction: column; gap: 6px }
        .pf-field-l {
          font-size: 12.5px; font-weight: 800; color: var(--cl-text);
          letter-spacing: .01em;
        }
        .pf-field input {
          background: rgba(0,0,0,.25);
          color: var(--cl-text);
          border: 1px solid var(--cl-line);
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14px; font-family: inherit;
          transition: border-color .15s, box-shadow .15s;
        }
        .pf-field input:focus {
          outline: 0;
          border-color: var(--cl-teal);
          box-shadow: 0 0 0 3px rgba(0,212,224,.18);
        }
        .pf-field input:disabled {
          background: rgba(0,0,0,.15);
          color: var(--cl-muted);
          cursor: not-allowed;
        }
        .pf-field input::placeholder { color: var(--cl-muted2, #6b7d92) }
        .pf-hint { font-size: 11.5px; color: var(--cl-muted); line-height: 1.4 }

        .pf-err {
          background: rgba(248, 113, 113, .12);
          border: 1px solid rgba(248, 113, 113, .35);
          color: #fca5a5;
          padding: 10px 14px; border-radius: 10px;
          font-size: 13px;
        }
        .pf-ok {
          background: rgba(34, 197, 94, .12);
          border: 1px solid rgba(34, 197, 94, .35);
          color: #86efac;
          padding: 10px 14px; border-radius: 10px;
          font-size: 13px;
        }

        .pf-actions { display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap }
        .pf-btn-primary {
          padding: 11px 22px; border-radius: 999px;
          background: var(--cl-teal); color: #002a30; border: 0;
          font-size: 13.5px; font-weight: 800; font-family: inherit;
          cursor: pointer; transition: transform .15s, box-shadow .15s;
        }
        .pf-btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 18px rgba(0,212,224,.25);
        }
        .pf-btn-primary:disabled { opacity: .6; cursor: not-allowed }
        .pf-btn-secondary {
          padding: 11px 18px; border-radius: 999px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          color: var(--cl-text); font-family: inherit;
          font-size: 13.5px; font-weight: 700;
          cursor: pointer; transition: border-color .15s, background .15s;
        }
        .pf-btn-secondary:hover {
          border-color: var(--cl-line2, rgba(255,255,255,.18));
          background: rgba(0,0,0,.20);
        }
      `}</style>
    </ClienteLayout>
  )
}
